import React, { useState, useRef } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../lib/firebase';
import { Upload, X, CheckCircle, File } from 'lucide-react';
import toast from 'react-hot-toast';

interface FileUploadProps {
  jiraKey: string;
  fieldName: string;
  acceptedTypes?: string[];
  maxSize?: number;
  onUploadComplete: (url: string) => void;
  currentValue?: string | null;
  readonly?: boolean;
}

export function FileUpload({
  jiraKey,
  fieldName,
  acceptedTypes = ['image/png', 'image/jpeg', 'image/svg+xml'],
  maxSize = 5 * 1024 * 1024, // 5MB
  onUploadComplete,
  currentValue,
  readonly = false,
}: FileUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    // Validate file type
    if (!acceptedTypes.includes(file.type)) {
      toast.error(`Invalid file type. Accepted types: ${acceptedTypes.join(', ')}`);
      return;
    }

    // Validate file size
    if (file.size > maxSize) {
      toast.error(`File too large. Maximum size: ${Math.round(maxSize / 1024 / 1024)}MB`);
      return;
    }

    setUploading(true);
    setProgress(0);
    setImageLoadError(false);

    try {
      const storageRef = ref(storage, `projects/${jiraKey}/${fieldName}/${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setProgress(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          toast.error('Upload failed');
          setUploading(false);
          setProgress(0);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          onUploadComplete(downloadURL);
          toast.success('File uploaded successfully');
          setUploading(false);
          setProgress(0);
        }
      );
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const removeFile = () => {
    onUploadComplete('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive && !readonly
            ? 'border-primary-500 bg-primary-50'
            : readonly
            ? 'border-gray-200 bg-gray-50'
            : 'border-gray-300 hover:border-gray-400'
        } ${uploading ? 'pointer-events-none opacity-50' : ''} ${readonly ? 'pointer-events-none' : ''}`}
        onDragEnter={readonly ? undefined : handleDrag}
        onDragLeave={readonly ? undefined : handleDrag}
        onDragOver={readonly ? undefined : handleDrag}
        onDrop={readonly ? undefined : handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={acceptedTypes.join(',')}
          onChange={handleChange}
          disabled={uploading || readonly}
        />

        {currentValue && !uploading ? (
          <div className="space-y-2">
            <div className="flex items-center justify-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-sm text-gray-600">File uploaded successfully</span>
            </div>
            
            {/* File Preview */}
            {currentValue.includes('firebasestorage.googleapis.com') && (
              <div className="flex justify-center">
                {!imageLoadError ? (
                  <img 
                    src={currentValue} 
                    alt="File preview" 
                    className="max-h-32 max-w-full rounded border border-gray-200 object-contain"
                    onError={() => {
                      setImageLoadError(true);
                    }}
                    onLoad={() => {
                      setImageLoadError(false);
                    }}
                  />
                ) : (
                  <div className="flex flex-col items-center space-y-2 p-4 border border-gray-200 rounded-lg bg-gray-50">
                    <File className="h-8 w-8 text-gray-400" />
                    <span className="text-sm text-gray-600">File uploaded</span>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex items-center justify-center space-x-2">
              {!readonly && (
                <button
                  type="button"
                  onClick={removeFile}
                  className="text-sm text-red-600 hover:text-red-700 flex items-center space-x-1"
                >
                  <X className="h-4 w-4" />
                  <span>Remove file</span>
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <Upload className="mx-auto h-8 w-8 text-gray-400" />
            <div className="text-sm text-gray-600">
              {readonly ? (
                <span className="text-gray-500">File upload disabled</span>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="font-medium text-primary-600 hover:text-primary-500 cursor-pointer"
                  >
                    Click to upload
                  </button>{' '}
                  or drag and drop
                </>
              )}
            </div>
            <p className="text-xs text-gray-500">
              Accepted types: {acceptedTypes.join(', ')} (max {Math.round(maxSize / 1024 / 1024)}MB)
            </p>
          </div>
        )}

        {uploading && (
          <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Uploading... {Math.round(progress)}%</p>
            </div>
          </div>
        )}
      </div>

      {currentValue && (
        <div className="text-xs text-gray-500">
          Current file: {decodeURIComponent(currentValue.split('/').pop()?.split('?')[0] || '')}
        </div>
      )}
    </div>
  );
}
